/* ═══════════════════════════════════════════════════════════════════════════
 * LES MÉTA-DONNÉES SUIVENT LA NAVIGATION — lot visibilité, 14/10/2026
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ LE PRÉ-RENDU NE SUFFIT PAS, ET IL FAUT DIRE POURQUOI. Le HTML statique
 *    porte les bonnes balises **à l'arrivée** ; mais dès que le visiteur clique
 *    d'une page à l'autre, l'application ne recharge rien et le `<title>` reste
 *    celui de la page d'entrée. ➡️ Ce composant les repose à chaque changement
 *    de route.
 * ⓘ MÊME SOURCE QUE LE PRÉ-RENDU : `src/seo/pages.js`. *Deux rédactions
 *   auraient divergé, et c'est la version du robot qu'on aurait oubliée.*
 * ⛔ IL N'ÉCRIT RIEN HORS DES SIX ROUTES PUBLIQUES. Sur l'application connectée,
 *    il remet simplement le titre du site : ces pages ne sont pas indexables, et
 *    y poser une description de vitrine serait faux.
 * ⓘ Aucune dépendance ajoutée : `document.head` suffit, et une bibliothèque de
 *   plus pour écrire six balises serait un poids sans contrepartie.
 * ═══════════════════════════════════════════════════════════════════════════ */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  SITE, NOM_SITE, LOCALE, OG_IMAGE, OG_IMAGE_LARGEUR, OG_IMAGE_HAUTEUR, pageDe,
} from './pages';

/** Pose (ou crée) une balise `<meta>` identifiée par `name=` ou `property=`. */
function meta(cle, valeur, parPropriete) {
  const attribut = parPropriete ? 'property' : 'name';
  let el = document.head.querySelector(`meta[${attribut}="${cle}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attribut, cle);
    document.head.appendChild(el);
  }
  el.setAttribute('content', valeur);
}

function canonique(url) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', url);
}

export default function Seo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const p = pageDe(pathname);
    if (!p) {
      /* ⓘ Hors vitrine : le titre du site, et rien de plus. On ne laisse PAS la
       *   description de la page d'entrée traîner sur un écran de travail. */
      document.title = NOM_SITE;
      return;
    }
    const url = `${SITE}${p.chemin === '/' ? '/' : p.chemin}`;
    document.title = p.titre;
    meta('description', p.description);
    canonique(url);
    meta('og:type', p.chemin === '/' ? 'website' : 'article', true);
    meta('og:site_name', NOM_SITE, true);
    meta('og:locale', LOCALE, true);
    meta('og:title', p.ogTitre, true);
    meta('og:description', p.description, true);
    meta('og:url', url, true);
    meta('og:image', OG_IMAGE, true);
    meta('og:image:width', String(OG_IMAGE_LARGEUR), true);
    meta('og:image:height', String(OG_IMAGE_HAUTEUR), true);
    meta('og:image:alt', `${NOM_SITE} — ${p.ogTitre}`, true);
    meta('twitter:card', 'summary_large_image');
    meta('twitter:title', p.ogTitre);
    meta('twitter:description', p.description);
    meta('twitter:image', OG_IMAGE);
  }, [pathname]);

  return null;
}
